<template>
  <div ref="resizeRef" class="flex flex-row grow h-12">
    <svg ref="svgRef" class="h-full w-full" />
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watchEffect, computed } from 'vue'
import { select, scaleLinear } from 'd3'
import useLotStore from '@/stores/LotStore'
import { useResizeObserver } from '@/composables/resizeObserver'

const lotStore = useLotStore()

const svgRef = ref(null)
const { resizeRef, resizeState } = useResizeObserver()

const low = computed(() => {
  if (lotStore.lots.length === 1) {
    if (
      lotStore.lots[0].oilComponents?.totalOil !== undefined &&
      lotStore.lots[0].brewingRanges?.oilLow !== undefined
    ) {
      if (lotStore.lots[0].oilComponents?.totalOil < lotStore.lots[0].brewingRanges?.oilLow) {
        return Math.floor(lotStore.lots[0].oilComponents?.totalOil)
      }
    }
    return lotStore.lots[0].brewingRanges?.oilLow ?? 0
  }
  return 0
})

const rangeLow = computed(() => {
  if (lotStore.lots.length === 1) {
    return lotStore.lots[0].brewingRanges?.oilLow ?? 0
  }
  return 0
})

const high = computed(() => {
  if (lotStore.lots.length === 1) {
    if (lotStore.lots[0].oilComponents?.totalOil !== undefined) {
      if (
        lotStore.lots[0].oilComponents?.totalOil > (lotStore.lots[0].brewingRanges?.oilHigh ?? 8)
      ) {
        return Math.ceil(
          lotStore.lots[0].oilComponents?.totalOil + lotStore.lots[0].oilComponents?.totalOil * 0.05
        )
      }
    }
    return lotStore.lots[0].brewingRanges?.oilHigh ?? 8
  }
  return 28
})

const rangeHigh = computed(() => {
  if (lotStore.lots.length === 1) {
    return lotStore.lots[0].brewingRanges?.oilHigh ?? 0
  }
  return 0
})

const avg = computed(() => {
  if (lotStore.lots.length === 1) {
    return lotStore.lots[0].brewingRanges?.oilAve ?? null
  }
  return 0
})

const totalOil = computed(() => {
  if (lotStore.lots.length === 1) {
    return lotStore.lots[0].oilComponents?.totalOil ?? 0
  }
  return 0
})

onMounted(() => {
  const svg = select(svgRef.value)
  watchEffect(() => {
    const { width, height } = resizeState.dimensions
    // Clear out SVG on resize
    svg.text('')
    const fivePercent = (high.value - low.value) * 0.05
    const scaleLow = low.value - fivePercent
    const scaleHigh = high.value + fivePercent
    const center = height / 2

    // X axis data scaler
    const xScaler = scaleLinear()
      .domain([scaleLow, scaleHigh])
      .range([35, width - 35])

    svg
      .append('rect')
      .attr('x', xScaler(rangeLow.value))
      .attr('y', center - 4)
      .attr('width', xScaler(rangeHigh.value) - xScaler(rangeLow.value))
      .attr('height', 8)
      .attr('stroke', 'none')
      .attr('fill', 'rgba(42,47,87,0.22)')

    // Horizontal Line
    svg
      .append('line')
      .attr('y1', center)
      .attr('y2', center)
      .attr('x1', xScaler(scaleLow))
      .attr('x2', xScaler(scaleHigh))
      .attr('stroke-width', 2)
      .attr('stroke', 'var(--ych-grey-400)')

    // Left bound line
    svg
      .append('line')
      .attr('y1', center + 8)
      .attr('y2', center - 8)
      .attr('x1', xScaler(scaleLow))
      .attr('x2', xScaler(scaleLow))
      .attr('stroke-width', 2)
      .attr('stroke', 'var(--ych-grey-400)')

    // Right bound line
    svg
      .append('line')
      .attr('y1', center + 8)
      .attr('y2', center - 8)
      .attr('x1', xScaler(scaleHigh))
      .attr('x2', xScaler(scaleHigh))
      .attr('stroke-width', 2)
      .attr('stroke', 'var(--ych-grey-400)')

    // Average value line
    if (avg.value !== null) {
      svg
        .append('line')
        .attr('y1', center + 4)
        .attr('y2', center - 4)
        .attr('x1', xScaler(avg.value))
        .attr('x2', xScaler(avg.value))
        .attr('stroke-width', 2)
        .attr('stroke', 'var(--ych-grey-400)')
    }

    // Total Oil
    svg
      .append('line')
      .attr('y1', center + 6)
      .attr('y2', center - 6)
      .attr('x1', xScaler(totalOil.value))
      .attr('x2', xScaler(totalOil.value))
      .attr('stroke', 'var(--ych-kraft-dark)')
      .attr('stroke-width', 4)
      .style('stroke-linecap', 'round')

    // Low Value
    svg
      .append('text')
      .attr('x', 25)
      .attr('y', height / 2)
      .attr('dy', '.35em')
      .style('text-anchor', 'end')
      .text(low.value)

    // High value
    svg
      .append('text')
      .attr('x', width - 25)
      .attr('y', height / 2)
      .attr('dy', '.35em')
      .text(high.value)
  })
})
</script>
